/**
 * null, undefined, false and '' all mean "not set" in the settings forms
 * (a reset writes `null` where a switched-off toggle holds `false`), so
 * they must compare equal.
 */
const normalizeSettingsValue = (value: unknown): string => {
    if (value === null || value === undefined || value === false) {
        return ''
    }
    return typeof value === 'string' ? value : JSON.stringify(value)
}

/**
 * Whether the edited settings differ from the saved ones. Drives the
 * enabled state of Save/Apply so an untouched form cannot fire an update
 * (and an audit-log entry) for attributes that did not change.
 */
export const hasSettingsChanged = (
    current: object | null | undefined,
    saved: object | null | undefined,
): boolean => {
    const currentValues = (current ?? {}) as Record<string, unknown>
    const savedValues = (saved ?? {}) as Record<string, unknown>
    const keys = new Set([
        ...Object.keys(currentValues),
        ...Object.keys(savedValues),
    ])
    return [...keys].some(
        (key) =>
            normalizeSettingsValue(currentValues[key]) !==
            normalizeSettingsValue(savedValues[key]),
    )
}

/**
 * Same as `hasSettingsChanged` for one level of grouping, such as template
 * permissions keyed by role and then by permission.
 */
export const hasGroupedSettingsChanged = (
    current: Record<string, Record<string, unknown>> | null | undefined,
    saved: Record<string, Record<string, unknown>> | null | undefined,
): boolean => {
    const groups = new Set([
        ...Object.keys(current ?? {}),
        ...Object.keys(saved ?? {}),
    ])
    return [...groups].some((group) =>
        hasSettingsChanged(current?.[group], saved?.[group]),
    )
}

/**
 * Whether any edited field differs from the record it was edited from. Only
 * the edited keys are compared, so ids and sequences the record carries but
 * the form never shows do not count as changes.
 */
export const hasEditedFieldsChanged = (
    edited: Record<string, unknown>,
    original: object | null | undefined,
): boolean => {
    const originalValues = (original ?? {}) as Record<string, unknown>
    return hasSettingsChanged(
        edited,
        Object.fromEntries(
            Object.keys(edited).map((key) => [key, originalValues[key]]),
        ),
    )
}
